/**
 * dopamiles-cart-helpers.js
 * Phase 05 strangler-split: pure utility helpers extracted from dopamiles-cart.js.
 *
 * Exported as window.bncCartHelpers (IIFE, no bundler).
 * Loaded with defer BEFORE dopamiles-cart-mutations.js and dopamiles-cart.js.
 *
 * Exports:
 *   escapeHtml(s)          — minimal HTML-escape for safe innerHTML insertion
 *   updateBubbleCount(n)   — update header bag-count span
 *   publishCartUpdate(c)   — publish PUB_SUB_EVENTS.cartUpdate
 *   parseSectionDoc(html)  — parse HTML string via DOMParser, null-guarded
 */

(function () {
  'use strict';

  /**
   * Minimal HTML-escape to prevent XSS when inserting server-returned
   * error messages into innerHTML (used by error-banner paths in cart.js).
   * Only escapes the five characters that can break HTML context.
   *
   * @param {string} s — raw string from server response
   * @returns {string} escaped string safe for innerHTML
   */
  function escapeHtml(s) {
    if (typeof s !== 'string') return '';
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Update the header bag-count span (#bnc-bag-count).
   *
   * The bag icon cannot be bundled via Section Render API — dopamiles-header.liquid
   * inlines it as <button id="bnc-bag-btn"> with <span id="bnc-bag-count">.
   * The count span is always present (rendered by Liquid), starts with
   * `hidden` attribute when cart is empty.
   *
   * Copied verbatim from dopamiles-cart.js (phase 05 extraction).
   *
   * @param {number} count
   */
  function updateBubbleCount(count) {
    const countEl = document.getElementById('bnc-bag-count');
    if (!countEl) return;
    countEl.textContent = count < 100 ? String(count) : '99+';
    countEl.setAttribute('aria-label', count + ' items in cart');
    if (count > 0) {
      countEl.removeAttribute('hidden');
    } else {
      countEl.setAttribute('hidden', '');
    }
  }

  /**
   * Publish PUB_SUB_EVENTS.cartUpdate via Dawn's pubsub system.
   *
   * Globals `publish` + `PUB_SUB_EVENTS` come from pubsub.js + constants.js
   * loaded with defer before this file (theme.liquid ordering).
   *
   * Copied verbatim from dopamiles-cart.js (phase 05 extraction).
   *
   * @param {object} cart — Shopify cart object
   */
  function publishCartUpdate(cart) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dopamiles-cart', cartData: cart });
    }
  }

  /**
   * Customer-facing item count, excluding the hidden Shipping Protection line.
   * SP renders as a checkbox widget (not a product row), so cart.item_count —
   * which counts it — reads one higher than the visible products. The SP
   * variant id is emitted on the drawer wrapper as data-sp-variant-id.
   *
   * @param {object} cart — Shopify cart object (needs .item_count, .items)
   * @returns {number} count excluding the SP line
   */
  function displayCount(cart) {
    if (!cart) return 0;
    var total = cart.item_count || 0;
    var drawer = document.getElementById('bnc-cart-drawer');
    var spId = drawer && drawer.dataset ? drawer.dataset.spVariantId : null;
    if (!spId || !Array.isArray(cart.items)) return total;
    var spQty = 0;
    cart.items.forEach(function (it) {
      if (String(it.variant_id) === String(spId)) spQty += (it.quantity || 0);
    });
    return Math.max(0, total - spQty);
  }

  /**
   * Parse an HTML string via DOMParser, with null-guard.
   * Centralises all DOMParser instantiation so mutations.js and cart.js
   * don't each carry a `new DOMParser()` call inline.
   *
   * @param {string} html — raw HTML string (e.g. from Shopify Section Render API)
   * @returns {Document|null} parsed document, or null if html is falsy
   */
  function parseSectionDoc(html) {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html');
  }

  // ── Export ────────────────────────────────────────────────────────────────

  window.bncCartHelpers = {
    escapeHtml: escapeHtml,
    updateBubbleCount: updateBubbleCount,
    displayCount: displayCount,
    publishCartUpdate: publishCartUpdate,
    parseSectionDoc: parseSectionDoc,
  };

})();
